import { type JSX } from "react";
import { AxiosInstance } from "axios";

import { useListRateSchedulesWithRenewal } from "../../hooks/useRates";

import { RateSetsPanel } from "./RateSetsPanel";

interface RateSetsSectionProps {
  /** When false the section renders nothing (e.g. carrier plans, Build tab). */
  enabled: boolean;
  benefitPlanId: string | undefined;
  axiosInstance: AxiosInstance | undefined;
  onSaved?: () => void;
}

interface RateSetsSectionContentProps {
  benefitPlanId: string;
  axiosInstance: AxiosInstance;
  onSaved?: () => void;
}

/**
 * Fetches the plan's renewal-inclusive schedules and renders the rate sets panel.
 * Split from the gate so the panel and the read hook always receive a defined plan id
 * and axios instance.
 */
const RateSetsSectionContent = ({
  benefitPlanId,
  axiosInstance,
  onSaved,
}: RateSetsSectionContentProps): JSX.Element => {
  const { data } = useListRateSchedulesWithRenewal(
    benefitPlanId,
    axiosInstance,
  );

  return (
    // Key by plan so switching the drawer's plan sub-tab remounts the panel — its
    // active rate-set tab and draft reset instead of carrying over the prior plan's
    // (e.g. staying on a Revised Renewal tab that's disabled for the new plan).
    <RateSetsPanel
      key={benefitPlanId}
      benefitPlanId={benefitPlanId}
      schedules={data ?? []}
      axiosInstance={axiosInstance}
      onSaved={onSaved}
    />
  );
};

/**
 * Gate for the renewal / revised-renewal rate sets. Renders nothing unless it's
 * enabled for a real plan with an axios instance.
 */
export const RateSetsSection = ({
  enabled,
  benefitPlanId,
  axiosInstance,
  onSaved,
}: RateSetsSectionProps): JSX.Element | null => {
  if (!enabled || !benefitPlanId || !axiosInstance) return null;

  return (
    <RateSetsSectionContent
      benefitPlanId={benefitPlanId}
      axiosInstance={axiosInstance}
      onSaved={onSaved}
    />
  );
};

export default RateSetsSection;
